import React, { useEffect, useRef, useState } from 'react';
import Table, { Column, TableHandle } from '../../../components/Table';
import { LightColors } from '../../../utils/colors/colors';
import { FaTrash, FaEnvelope, FaPhoneAlt, FaFileExcel } from 'react-icons/fa';
import { formatIndianNumber } from '../../../helper/numberFormate';
import { apiGet, apiPost } from '../../../services/apiServices';
import { endPoints } from '../../../configs/apiUrls';
import { toast } from 'react-toastify';
import Modal from '../../../components/Modal';
import * as XLSX from 'xlsx'; // ✅ Import xlsx for Excel export

// Import the extracted filter component
import AgentSearchFilterForm from './AgentSearchFilterForm';
import { Button } from '../../../components/Button';

const AgentSearches = () => {
  const tableRef = useRef<TableHandle>(null);

  // Local State Management
  const [searchList, setSearchList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false); // ✅ Export loading state
  const [refresh, setRefresh] = useState(false)

  const [filterData, setFilterData] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
    search: "",
    sortBy: "",
    sortOrder: "",
    state: "",
    city: "",
    area: "",
    street: "",
    fromDate: "",
    toDate: "",
  });

  // Selection State for Bulk Delete
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Modal States
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Fetch Data API Call
  // ✅ Now returns data if isExport is true
  const fetchSearches = async (isExport: boolean = false) => {
    if (!isExport) setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: filterData.page.toString(),
        limit: isExport ? "null" : filterData.limit.toString(), // "null" fetches all if backend supports it
        ...(filterData.search && { search: filterData.search }),
        ...(filterData.sortBy && { sortBy: filterData.sortBy }),
        ...(filterData.sortOrder && { sortOrder: filterData.sortOrder }),
        ...(filterData.state && { state: filterData.state }),
        ...(filterData.city && { city: filterData.city }),
        ...(filterData.area && { area: filterData.area }),
        ...(filterData.street && { street: filterData.street }),
        ...(filterData.fromDate && { fromDate: filterData.fromDate }),
        ...(filterData.toDate && { toDate: filterData.toDate }),
      })?.toString();

      const res: any = await apiGet(`${endPoints.GET_AGENT_SEARCHES}?${queryParams}`);

      if (res?.success) {
        if (isExport) {
          return res.data; // ✅ Return data for Excel export
        }
        setSearchList(res?.data || []);
        setFilterData(prev => ({ ...prev, totalCount: res?.total || 0 }));
      }
    } catch (error) {
      console.error("Failed to fetch agent searches", error);
      if (!isExport) toast.error("Failed to load agent search history");
    } finally {
      if (!isExport) setLoading(false);
    }
  };

  useEffect(() => {
    fetchSearches();
  }, [
    filterData.page,
    filterData.limit,
    filterData.search,
    filterData.sortBy,
    filterData.sortOrder,
    filterData.state,
    filterData.city,
    filterData.area,
    filterData.street,
    filterData.fromDate,
    filterData.toDate,
    refresh
  ]);

  // ==========================================
  // 📌 EXPORT TO EXCEL FUNCTION
  // ==========================================
  const handleExport = async () => {
    try {
      setIsExporting(true);
      const rawData = await fetchSearches(true); // Fetch all data

      if (!rawData || rawData.length === 0) {
        toast.warning("No data available to export");
        return;
      }

      // Format data into clean rows for Excel
      const formattedData = rawData.map((item: any) => ({
        "Agent Name": item.agentId?.name || "N/A",
        "Agent Email": item.agentId?.email || "N/A",
        "Agent Phone": item.agentId?.mobileNumber || "N/A",
        "State": item.state || "N/A",
        "City": item.city || "N/A",
        "Area": item.area?.area || item.area || "N/A",
        "Street": item.street?.street || item.street || "N/A",
        "Survey No.": item.surveyNumber || "N/A",
        "Property Type": item.propertyType || "N/A",
        "Area SqFt": item.areaSqFt || 0,
        "Market Rate (₹)": item.marketRate || 0,
        "Valuation Found": item.isValuationFound ? "Yes" : "No",
        "Search Date": item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A",
      }));

      // Create Excel Worksheet and Workbook
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Agent Searches");

      // Trigger Download
      XLSX.writeFile(workbook, `Agent_Searches_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success("Export successful!");

    } catch (error) {
      console.error("Export Error:", error);
      toast.error("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  // Execute Bulk Delete
  const confirmDelete = async () => {
    if (selectedIds.length === 0) return;

    try {
      setLoading(true);

      const res: any = await apiPost(`${endPoints.DELETE_AGENT_SEARCH}/bulk-delete`, {
        ids: selectedIds
      });

      if (res?.success) {
        toast.success(res.message || `${selectedIds.length} record(s) deleted successfully`);

        if (tableRef.current) {
          tableRef.current.clearSelection();
        }
        setSelectedIds([]);

        fetchSearches();
      } else {
        toast.error(res?.message || "Failed to delete records");
      }

    } catch (error) {
      console.error("Error deleting records", error);
      toast.error("Something went wrong while deleting");
    } finally {
      setShowDeleteModal(false);
      setLoading(false);
    }
  };

  const columns: Column<any>[] = [
    {
      key: "agentId",
      label: "Agent Name",
      customCell: (row) => (
        <div
          className='flex flex-col cursor-pointer hover:underline text-blue-600'
          onClick={() => {
            setSelectedAgent(row?.agentId);
            setShowAgentModal(true);
          }}
        >
          <span className="font-semibold">{row?.agentId?.name || 'View Agent'}</span>
        </div>
      )
    },
    { key: "state", label: "State" },
    { key: "city", label: "City" },
    {
      key: "area", label: "Area", customCell: (row) => (
        <div className='flex flex-col text-sm'>
          <span>{row?.area?.area}</span>
        </div>
      )
    },
    {
      key: "Street", label: "street", customCell: (row) => (
        <div className='flex flex-col text-sm'>
          <span>{row?.street?.street}</span>
        </div>
      )
    },
    { key: "surveyNumber", label: "Survey No." },
    {
      key: "marketRate", label: "Market Rate",
      customCell: (row) => (
        <div className='flex flex-col text-sm'>
          <span>{formatIndianNumber(row?.marketRate || 0)}</span>
        </div>
      )
    },
  ];

  const onSortChanged = (sortBy: any, sortOrder: any) => {
    setFilterData(prev => ({
      ...prev,
      sortBy: sortBy,
      sortOrder: sortOrder
    }));
  };

  const closeAgentModal = () => {
    setShowAgentModal(false);
    setSelectedAgent(null);
  };

  return (
    <div className="relative">
      <Table
        ref={tableRef}
        columns={columns as any}
        data={searchList}
        tableName="Agent Search History"
        totalCount={filterData.totalCount}
        currentPage={filterData.page}
        pageSize={filterData.limit}
        onSearchChange={(searchText) => {
          setFilterData(prev => ({ ...prev, search: searchText, page: 1 }));
        }}
        onPageChange={(page) => {
          setFilterData(prev => ({ ...prev, page: page }));
        }}
        onPageSizeChange={(size: number) => {
          setFilterData(prev => ({ ...prev, limit: size, page: 1 }));
        }}
        onSortChange={onSortChanged}
        loading={loading}

        isSelectable={true}
        rowKey="_id"
        onSelectionChange={(selectedRows) => {
          const ids = selectedRows.map(row => row._id);
          setSelectedIds(ids);
        }}

        filterContent={
          <AgentSearchFilterForm
            tableRef={tableRef}
            filterData={filterData}
            setFilterData={setFilterData}
          />
        }

        // ✅ Export Button added to the Left side of the table toolbar
        renderLeftToolItems={
          <button
            onClick={handleExport}
            disabled={isExporting || filterData.totalCount === 0}
            className={`ml-4 flex items-center px-4 py-2 font-medium rounded-lg transition-colors shadow-sm ${isExporting || filterData.totalCount === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
              }`}
          >
            <FaFileExcel className="mr-2" size={14} />
            {isExporting ? "Exporting..." : "Export Excel"}
          </button>
        }

        renderRightToolItems={
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={() => setRefresh(!refresh)}
            >
              Refresh
            </Button>

            {
              selectedIds.length > 0 && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="ml-4 flex items-center px-4 py-2 bg-red-50 text-red-600 border border-red-200 font-medium rounded-lg hover:bg-red-100 hover:text-red-700 transition-colors shadow-sm"
                >
                  <FaTrash className="mr-2" size={14} />
                  Delete Selected ({selectedIds.length})
                </button>
              )
            }
          </div>

        }
      />

      {/* AGENT INFO MODAL */}
      <Modal
        isOpen={showAgentModal}
        onClose={closeAgentModal}
        title=""
        size="sm"
      >
        <div className="pt-2 pb-2">
          {/* Header Profile Section */}
          <div className="flex items-center space-x-4 mb-6">
            <div className="h-16 w-16 bg-blue-50 text-blue-600 border border-blue-100 rounded-full flex items-center justify-center text-2xl font-bold shadow-sm">
              {selectedAgent?.name ? selectedAgent.name.charAt(0).toUpperCase() : 'A'}
            </div>

            <div className="flex flex-col">
              <h3 className="text-xl font-bold text-gray-900">
                {selectedAgent?.name || 'Unknown Agent'}
              </h3>
              <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-md font-medium mt-1 w-max border border-blue-100">
                Registered Agent
              </span>
            </div>
          </div>

          {/* Contact Information Card */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4 border border-gray-100 shadow-inner mb-6">
            {/* Email Row */}
            <div className="flex items-center">
              <div className="h-10 w-10 bg-white rounded-lg shadow-sm border border-gray-100 flex items-center justify-center mr-4 text-gray-400">
                <FaEnvelope size={16} />
              </div>
              <div className="flex flex-col">
                <span className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">
                  Email Address
                </span>
                <span className="text-sm font-semibold text-gray-800">
                  {selectedAgent?.email || 'Not Provided'}
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className="h-px w-full bg-gray-200"></div>

            {/* Phone Row */}
            <div className="flex items-center">
              <div className="h-10 w-10 bg-white rounded-lg shadow-sm border border-gray-100 flex items-center justify-center mr-4 text-gray-400">
                <FaPhoneAlt size={14} />
              </div>
              <div className="flex flex-col">
                <span className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">
                  Phone Number
                </span>
                <span className="text-sm font-semibold text-gray-800">
                  {selectedAgent?.mobileNumber || 'Not Provided'}
                </span>
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className="flex justify-end">
            <button
              className="w-full py-3 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 focus:outline-none focus:ring-4 focus:ring-gray-200 transition-all shadow-md active:scale-[0.98]"
              onClick={closeAgentModal}
            >
              Close Details
            </button>
          </div>
        </div>
      </Modal>

      {/* DELETE CONFIRMATION MODAL */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirm Deletion"
        size="sm"
      >
        <div className="mt-2 mb-6 text-gray-700">
          <p>
            Are you sure you want to delete <strong>{selectedIds.length}</strong> selected record(s)?
            This action cannot be undone.
          </p>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            className="px-5 py-2 bg-gray-200 text-gray-800 font-medium rounded-lg hover:bg-gray-300 focus:outline-none transition-colors"
            onClick={() => setShowDeleteModal(false)}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="px-5 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors disabled:opacity-50 flex items-center"
            onClick={confirmDelete}
            disabled={loading}
          >
            {loading ? "Deleting..." : "Yes, Delete"}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default AgentSearches;